import type { LocaleCode } from "./locale-store";
import type { ShellNotification } from "./notification-store";

export interface PresentedNotificationContent {
  readonly title: string;
  readonly body: string;
}

export type NotificationTone = "danger" | "info" | "neutral" | "warning";

const MISSION_ORDER_TYPE_CODES = new Set([
  "mission_order_assigned",
  "mission_order_assigned_team",
  "mission_order_issued",
]);

const MISSION_ORDER_TITLES = new Set([
  "Mission Order assigned",
  "Mission Order issued",
  "New Mission Order",
  "Neuer Einsatzauftrag",
]);

const MISSION_ORDER_BODY_STARTS = [
  "A new Mission Order is available.",
  "Ein neuer Einsatzauftrag liegt vor.",
] as const;

const REDACTED_NOTIFICATION_CONTENT = {
  body: "Open the authenticated application to view this notification.",
  title: "New notification",
} as const;

const HAZARD_TITLE =
  /^(Escalation: )?Critical adviser hazard \((HIGH|CRITICAL)\)$/u;
const HAZARD_BODY =
  /^Immediate safety review required at (.+)\.$/su;

const REGIONAL_SUPPORT_TITLE = "Regional support request";
const REGIONAL_SUPPORT_BODY =
  "A regional support request is waiting for the supporting LuK's StAN, actual availability and readiness assessment.";
const REGIONAL_SUPPORT_RESPONSE_CONTENT = {
  ACCEPTED: {
    body:
      "The supporting LuK accepted the regional request and created its linked local incident.",
    title: "Regional support accepted",
  },
  DECLINED: {
    body:
      "The supporting LuK declined the regional request. Further coordination may be required.",
    title: "Regional support declined",
  },
} as const;

const INCIDENT_LIFECYCLE_BODY =
  /^Der Einsatz(?: „([^“]+)“)? ist jetzt: (Aktiv|Unter Kontrolle|Demobilisierung|Geschlossen|Abgebrochen|Entwurf|Gemeldet|Geprüft|DRAFT|REPORTED|VALIDATED)\.$/u;

const INCIDENT_STATE_LABELS: Readonly<Record<string, readonly [string, string]>> = {
  Abgebrochen: ["Abgebrochen", "Cancelled"],
  Aktiv: ["Aktiv", "Active"],
  Demobilisierung: ["Demobilisierung", "Demobilizing"],
  DRAFT: ["Entwurf", "Draft"],
  Entwurf: ["Entwurf", "Draft"],
  Geschlossen: ["Geschlossen", "Closed"],
  Gemeldet: ["Gemeldet", "Reported"],
  Geprüft: ["Geprüft", "Validated"],
  REPORTED: ["Gemeldet", "Reported"],
  "Unter Kontrolle": ["Unter Kontrolle", "Contained"],
  VALIDATED: ["Geprüft", "Validated"],
};

const LOGISTICS_SUBMITTED_BODY =
  /^(.+) „(.+)“ \((Sofort|Dringend|Normal)\) von (.+) am Einsatz- oder Übergabeort „(.+)“ wurde an LuK\/S4 übermittelt\.$/su;
const LOGISTICS_UPDATED_BODY =
  /^(.+) „(.+)“ hat einen neuen Bearbeitungsstand: (.+)\.$/su;
const LOGISTICS_RECEIPT_BODY =
  /^(.+) „(.+)“ wurde am Übergabepunkt „(.+)“ durch (.+) empfangen\.$/su;

const LOGISTICS_KIND_EN: Readonly<Record<string, string>> = {
  "Ablösung / Schichtwechsel": "Relief / shift change",
  Betriebsstoff: "Fuel",
  Kräfteanforderung: "Force request",
  Materialanforderung: "Material request",
  Transport: "Transport",
  Unterbringung: "Accommodation",
  Verpflegung: "Catering",
};

const LOGISTICS_PRIORITY_EN: Readonly<Record<string, string>> = {
  Dringend: "Urgent",
  Normal: "Normal",
  Sofort: "Immediate",
};

const LOGISTICS_STATE_EN: Readonly<Record<string, string>> = {
  "in Bearbeitung": "in process",
  "nicht erfüllt": "unfulfilled",
  "teilweise erfüllt": "partially fulfilled",
  "übermittelt": "submitted",
  "erfüllt": "fulfilled",
  "storniert": "cancelled",
  Entwurf: "draft",
};

function raw(item: ShellNotification): PresentedNotificationContent {
  return { body: item.body, title: item.title };
}

function missionOrderContent(
  locale: LocaleCode,
): PresentedNotificationContent {
  return locale === "de"
    ? {
        title: "Neuer Einsatzauftrag",
        body:
          "Ein neuer Einsatzauftrag liegt vor. Zugeordnete Einsatzkräfte erhalten ihn zur Kenntnis; nur die verantwortliche Führung bestätigt verbindlich.",
      }
    : {
        title: "New Mission Order",
        body:
          "A new Mission Order is available. Assigned personnel receive it for information; only the responsible leader acknowledges it formally.",
      };
}

function hazardContent(
  locale: LocaleCode,
  severity: "HIGH" | "CRITICAL",
  location: string,
  escalated: boolean,
): PresentedNotificationContent {
  const escalationPrefix = escalated
    ? locale === "de" ? "Eskalation: " : "Escalation: "
    : "";
  return locale === "de"
    ? {
        title: `${escalationPrefix}Kritische Gefahrenmeldung der Fachberatung (${severity})`,
        body: `Sofortige Sicherheitsprüfung erforderlich: ${location}.`,
      }
    : {
        title: `${escalationPrefix}Critical adviser hazard (${severity})`,
        body: `Immediate safety review required at ${location}.`,
      };
}

/**
 * Produces locale-specific display text for notification templates the web
 * client understands. The persisted M23 notification remains the immutable
 * audit record. Redacted, unknown, or changed templates are always rendered
 * verbatim so the presentation layer cannot reconstruct protected content or
 * accidentally reinterpret a newer server message.
 */
export function presentNotification(
  item: ShellNotification,
  locale: LocaleCode,
): PresentedNotificationContent {
  if (item.redacted) {
    if (
      item.title === REDACTED_NOTIFICATION_CONTENT.title &&
      item.body === REDACTED_NOTIFICATION_CONTENT.body
    ) {
      return locale === "de"
        ? {
            title: "Neue Benachrichtigung",
            body:
              "Öffnen Sie die angemeldete Anwendung, um diese Benachrichtigung anzuzeigen.",
          }
        : REDACTED_NOTIFICATION_CONTENT;
    }
    return raw(item);
  }

  if (
    MISSION_ORDER_TYPE_CODES.has(item.typeCode) &&
    MISSION_ORDER_TITLES.has(item.title) &&
    MISSION_ORDER_BODY_STARTS.some((start) => item.body.startsWith(start))
  ) {
    return missionOrderContent(locale);
  }

  if (item.typeCode === "m08.critical_hazard") {
    const title = HAZARD_TITLE.exec(item.title);
    const body = HAZARD_BODY.exec(item.body);
    if (title !== null && body !== null) {
      const severity = title[2];
      const location = body[1];
      if (
        (severity !== "HIGH" && severity !== "CRITICAL") ||
        location === undefined
      ) {
        return raw(item);
      }
      return hazardContent(
        locale,
        severity,
        location,
        title[1] !== undefined,
      );
    }
  }

  if (
    item.typeCode === "regional_support_request" &&
    item.title === REGIONAL_SUPPORT_TITLE &&
    item.body === REGIONAL_SUPPORT_BODY
  ) {
    return locale === "de"
      ? {
          title: "Regionale Unterstützungsanfrage",
          body:
            "Eine regionale Unterstützungsanfrage wartet auf die getrennte Prüfung von StAN-Eignung, tatsächlicher Verfügbarkeit und Einsatzbereitschaft durch die unterstützende LuK.",
        }
      : { title: REGIONAL_SUPPORT_TITLE, body: REGIONAL_SUPPORT_BODY };
  }

  if (item.typeCode === "regional_support_response") {
    const outcome = Object.entries(REGIONAL_SUPPORT_RESPONSE_CONTENT).find(
      ([, content]) =>
        content.title === item.title && content.body === item.body,
    )?.[0] as keyof typeof REGIONAL_SUPPORT_RESPONSE_CONTENT | undefined;
    if (outcome !== undefined) {
      if (locale === "en") return REGIONAL_SUPPORT_RESPONSE_CONTENT[outcome];
      return outcome === "ACCEPTED"
        ? {
            title: "Regionale Unterstützung angenommen",
            body:
              "Die unterstützende LuK hat die regionale Anfrage angenommen und ihren verknüpften örtlichen Einsatz angelegt.",
          }
        : {
            title: "Regionale Unterstützung abgelehnt",
            body:
              "Die unterstützende LuK hat die regionale Anfrage abgelehnt. Weitere Koordinierung kann erforderlich sein.",
          };
    }
  }

  if (
    item.typeCode === "incident_lifecycle" &&
    (item.title === "Einsatzstatus geändert" || item.title === "Einsatz abgebrochen")
  ) {
    const match = INCIDENT_LIFECYCLE_BODY.exec(item.body);
    const stateLabels = match?.[2] === undefined
      ? undefined
      : INCIDENT_STATE_LABELS[match[2]];
    if (match !== null && stateLabels !== undefined) {
      const incidentTitle = match[1];
      const cancelled = match[2] === "Abgebrochen";
      return locale === "de"
        ? {
            title: cancelled ? "Einsatz abgebrochen" : "Einsatzstatus geändert",
            body: `Der Einsatz${incidentTitle === undefined ? "" : ` „${incidentTitle}“`} ist jetzt: ${stateLabels[0]}.`,
          }
        : {
            title: cancelled ? "Incident cancelled" : "Incident status changed",
            body: `Incident${incidentTitle === undefined ? "" : ` “${incidentTitle}”`} is now: ${stateLabels[1]}.`,
          };
    }
  }

  if (
    item.typeCode === "logistics_request_submitted" &&
    item.title === "Neue Einsatzanforderung"
  ) {
    const match = LOGISTICS_SUBMITTED_BODY.exec(item.body);
    if (match !== null) {
      const [, kind, requirement, priority, requester, location] = match;
      if (
        kind !== undefined &&
        requirement !== undefined &&
        priority !== undefined &&
        requester !== undefined &&
        location !== undefined
      ) {
        return locale === "de"
          ? raw(item)
          : {
              title: "New field request",
              body:
                `${LOGISTICS_KIND_EN[kind] ?? kind} “${requirement}” ` +
                `(${LOGISTICS_PRIORITY_EN[priority] ?? priority}) from ${requester} ` +
                `at “${location}” was submitted to LuK/S4.`,
            };
      }
    }
  }

  if (
    item.typeCode === "logistics_request_updated" &&
    item.title === "Einsatzanforderung aktualisiert"
  ) {
    const match = LOGISTICS_UPDATED_BODY.exec(item.body);
    if (match !== null) {
      const [, kind, requirement, state] = match;
      if (kind !== undefined && requirement !== undefined && state !== undefined) {
        return locale === "de"
          ? raw(item)
          : {
              title: "Field request updated",
              body:
                `${LOGISTICS_KIND_EN[kind] ?? kind} “${requirement}” now has the ` +
                `processing state: ${LOGISTICS_STATE_EN[state] ?? state}.`,
            };
      }
    }
  }

  if (
    item.typeCode === "logistics_receipt_confirmed" &&
    item.title === "Empfang der Einsatzversorgung bestätigt"
  ) {
    const match = LOGISTICS_RECEIPT_BODY.exec(item.body);
    if (match !== null) {
      const [, kind, requirement, location, requester] = match;
      if (
        kind !== undefined &&
        requirement !== undefined &&
        location !== undefined &&
        requester !== undefined
      ) {
        return locale === "de"
          ? raw(item)
          : {
              title: "Operational supply receipt confirmed",
              body:
                `${LOGISTICS_KIND_EN[kind] ?? kind} “${requirement}” was received ` +
                `by ${requester} at “${location}”.`,
            };
      }
    }
  }

  return raw(item);
}

/** Maps immutable notification facts to a theme-safe operational emphasis. */
export function notificationTone(item: ShellNotification): NotificationTone {
  if (item.typeCode === "m08.critical_hazard") return "danger";
  if (item.typeCode === "logistics_request_submitted") return "warning";
  if (item.acknowledgementRequired && item.acknowledgedAt === null) return "warning";
  if (
    MISSION_ORDER_TYPE_CODES.has(item.typeCode) ||
    item.typeCode === "regional_support_request" ||
    item.typeCode === "regional_support_response" ||
    item.typeCode.includes("chat") ||
    item.typeCode.includes("collaboration") ||
    item.typeCode.includes("incident")
  ) {
    return "info";
  }
  if (item.typeCode.startsWith("logistics_")) return "info";
  return "neutral";
}

/**
 * Returns only same-application routes. Notification content is persisted
 * server-side and must never be able to turn the shell into an external-link
 * launcher.
 */
export function notificationNavigationTarget(
  contextPath: string | null,
): string | null {
  if (
    contextPath === null ||
    contextPath.length === 0 ||
    !contextPath.startsWith("/") ||
    contextPath.startsWith("//") ||
    /[\u0000-\u001f\\]/u.test(contextPath)
  ) {
    return null;
  }
  try {
    const base = "https://thw-ims.invalid";
    const parsed = new URL(contextPath, base);
    if (parsed.origin !== base) return null;
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return null;
  }
}

export function notificationActionLabel(
  contextPath: string | null,
  locale: LocaleCode,
): string | null {
  const target = notificationNavigationTarget(contextPath);
  if (target === null) return null;
  if (target.startsWith("/modules/collaboration")) {
    return locale === "de" ? "Einsatzchat öffnen" : "Open incident chat";
  }
  if (target.includes("mission-order") || target.includes("mission_order")) {
    return locale === "de" ? "Einsatzauftrag öffnen" : "Open Mission Order";
  }
  if (target.includes("safety") || target.includes("adviser")) {
    return locale === "de" ? "Sicherheitsprüfung öffnen" : "Open safety review";
  }
  if (target.includes("supportRequestId=")) {
    return locale === "de" ? "LuK-Anfrage öffnen" : "Open LuK request";
  }
  if (target.includes("panel=logistics")) {
    return locale === "de" ? "Einsatzanforderung öffnen" : "Open field request";
  }
  if (target.startsWith("/operations") || target.includes("incident")) {
    return locale === "de" ? "Einsatz öffnen" : "Open incident";
  }
  return locale === "de" ? "Details öffnen" : "Open details";
}
